import Head from 'next/head';
import React, { useEffect, useMemo } from 'react';
import { signOut, useSession } from 'next-auth/react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { ActionCreators as UndoActionCreators } from 'redux-undo';
import mixpanel from 'mixpanel-browser';
import nookies from 'nookies';
import { useAssessmentAnalytics } from '../helpers/analytics';
import { serverEnvironmentVariablesToClientObject } from '../helpers/page-helpers';
import store from '../redux';
import { selectPresentMeta } from '../redux/meta';
import CurrentAssessmentComponent, {
  DevControls,
} from '../components/assessment';
import { ReduxFlowContainer } from '../components/assessment/shared';
import { AppDispatch, Screens } from '../redux/types';
import { getAdVendorQueryString } from '../advertising';
import { setLdFlags } from '../redux/ld-flags';
import { getLDFlags } from '../server/services/launch-darkly';
import {
  Experiments,
  preloadedExperiments,
  setExperiments,
} from '../redux/experiments';
import { FlagState } from '../experimentation/base';
import { getFlagStateForUser } from '../experimentation/server';

export const useAppDispatch = () => useDispatch<AppDispatch>();

type PropTypes = {
  ldFlags: object;
  experiments: Experiments;
};

export const ReduxAssessmentWithoutProvider = ({
  ldFlags,
  experiments,
}: PropTypes) => {
  const { data: nextAuthSession } = useSession();
  const dispatch = useAppDispatch();
  const meta = useSelector(selectPresentMeta);

  useEffect(() => {
    if (nextAuthSession?.user?.isAdmin) {
      void signOut({ redirect: false });
    }
  }, [nextAuthSession]);

  useEffect(() => {
    window.mixpanel = mixpanel;
    dispatch(setExperiments(experiments));
    dispatch(setLdFlags(ldFlags));
  }, []);

  useMemo(() => {
    performance.mark('pageload__assessment__rendering');
  }, []);

  const canGoBackWhenEditing = ['MI_InsurancePlan', 'MI_InsuranceId'];

  useAssessmentAnalytics(meta, meta.stepTitleIsNotPHI);

  window.scrollTo(0, 0);

  const searchParams = new URLSearchParams(window.location.search);
  const fbPixelUrl = `/fb-pixel?${getAdVendorQueryString(
    'Facebook',
    searchParams
  )}`;
  const tiktokPixelUrl = `/tiktok-pixel?${getAdVendorQueryString(
    'Tiktok',
    searchParams
  )}`;
  const podsightsPixelUrl = `/podsights-pixel?${getAdVendorQueryString(
    'Podsights',
    searchParams
  )}`;
  const blissPointPixelUrl = `/blisspoint-pixel?${getAdVendorQueryString(
    'BlissPointMedia',
    searchParams
  )}`;
  const everflowPixelUrl = `/analytics/everflow.html?${getAdVendorQueryString(
    'Everflow',
    searchParams
  )}`;
  const bingPixelUrl = `/analytics/bing.html?${getAdVendorQueryString(
    'Bing',
    searchParams
  )}`;
  const googlePixelUrl = `/analytics/google.html?${getAdVendorQueryString(
    'Google',
    searchParams
  )}`;
  const vwoPixelUrl = `/analytics/vwo.html?${getAdVendorQueryString(
    'VWO',
    searchParams
  )}`;

  return (
    <ReduxFlowContainer
      showPrivacyPolicy
      onBack={
        meta.canGoBack &&
        (meta.screen === Screens.Review ||
          !meta.review ||
          canGoBackWhenEditing.includes(meta.screen))
          ? () => {
              dispatch(UndoActionCreators.undo());
            }
          : undefined
      }
    >
      <Head>
        <title>Assessment</title>
      </Head>

      {/* This prop passing is not necessary, but opted for it for now over another
          useSelector in this component because I didn't want it to seem "magic" */}
      <CurrentAssessmentComponent screen={meta.screen} />

      {process.env.NEXT_PUBLIC_DD_ENV !== 'prod' && <DevControls />}
      <iframe
        src={fbPixelUrl}
        referrerPolicy="strict-origin"
        style={{ display: 'none' }}
        id="fbpixeliframe"
        title="fbpixel"
        className="analyticsSandbox"
        data-tid={process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID}
      />
      <iframe
        src={tiktokPixelUrl}
        referrerPolicy="strict-origin"
        style={{ display: 'none' }}
        id="tiktokpixeliframe"
        title="tiktokpixel"
        className="analyticsSandbox"
        data-tid={process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID}
      />
      <iframe
        src={podsightsPixelUrl}
        referrerPolicy="strict-origin"
        style={{ display: 'none' }}
        id="podsightspixeliframe"
        title="podsightspixel"
        className="analyticsSandbox"
        data-tid={process.env.NEXT_PUBLIC_PODSIGHTS_PIXEL_KEY}
      />
      <iframe
        src={blissPointPixelUrl}
        referrerPolicy="strict-origin"
        style={{ display: 'none' }}
        id="blisspointpixeliframe"
        title="blisspointpixel"
        className="analyticsSandbox"
        data-tid={process.env.NEXT_PUBLIC_BLISSPOINT_TAG_ID}
      />
      <iframe
        src={everflowPixelUrl}
        referrerPolicy="strict-origin"
        style={{ display: 'none' }}
        id="everflowpixeliframe"
        title="everflowpixel"
        className="analyticsSandbox"
        data-tid="everflow"
      />
      <iframe
        src={bingPixelUrl}
        referrerPolicy="strict-origin"
        style={{ display: 'none' }}
        id="bingpixeliframe"
        title="bingpixel"
        className="analyticsSandbox"
        data-tid={process.env.NEXT_PUBLIC_BING_PIXEL_ID}
      />
      <iframe
        src={googlePixelUrl}
        referrerPolicy="strict-origin"
        style={{ display: 'none' }}
        id="googlepixeliframe"
        title="googlepixel"
        className="analyticsSandbox"
        data-tid={process.env.NEXT_PUBLIC_GOOGLE_PIXEL_ID}
      />
      <iframe
        src={vwoPixelUrl}
        referrerPolicy="strict-origin"
        style={{ display: 'none' }}
        id="vwopixeliframe"
        title="vwopixel"
        className="analyticsSandbox"
        data-tid={process.env.NEXT_PUBLIC_VWO_ID}
      />
      {process.env.NEXT_PUBLIC_DD_ENV !== 'prod' && (
        <iframe
          src={`/analytics/test.html?${getAdVendorQueryString(
            'Test',
            searchParams
          )}`}
          referrerPolicy="strict-origin"
          style={{ display: 'none' }}
          id="testpixeliframe"
          title="testpixel"
          className="analyticsSandbox"
          data-tid="test"
        />
      )}
    </ReduxFlowContainer>
  );
};

// Note: we can move the Provider wrapper higher in the tree to avoid this
const ReduxAssessment = ({ ldFlags, experiments }) => (
  <Provider store={store}>
    <ReduxAssessmentWithoutProvider
      ldFlags={ldFlags}
      experiments={experiments}
    />
  </Provider>
);

const getExperiments = async (visitorCode: string) => {
  const flagPayload: Record<string, FlagState> = {};
  await preloadedExperiments.reduce(async (previousTask, flagName) => {
    await previousTask;

    const flagState = await getFlagStateForUser(
      flagName,
      [flagName],
      visitorCode
    );

    flagPayload[flagName] = flagState;
  }, Promise.resolve());
  return flagPayload;
};

export async function getServerSideProps(context) {
  const ldFlags = await getLDFlags({ clientSideOnly: true });
  const cookies = nookies.get(context);
  const experiments = await getExperiments(cookies.kameleoonVisitorCode);

  return {
    props: {
      dynamicEnvVars: serverEnvironmentVariablesToClientObject(),
      ldFlags,
      experiments,
    },
  };
}

export default ReduxAssessment;
